// CHDS Circle — referral capture on the storefront.
// A code the customer TYPES always beats a code they merely clicked (code_beats_link).
import { defineStore } from 'pinia';
import axios from '../../axios';

const KEY = 'chds_ref';
const DAYS = 30;

function writeCookie(code) {
  const d = new Date();
  d.setDate(d.getDate() + DAYS);
  document.cookie = `${KEY}=${encodeURIComponent(code)};expires=${d.toUTCString()};path=/;SameSite=Lax`;
  try { localStorage.setItem(KEY, code); } catch (e) { /* private mode */ }
}

function readCookie() {
  const hit = document.cookie.split('; ').find((r) => r.startsWith(`${KEY}=`));
  if (hit) return decodeURIComponent(hit.split('=')[1]);
  try { return localStorage.getItem(KEY) || ''; } catch (e) { return ''; }
}

const TOKEN_KEY = 'chds_circle_token';

export const useCircleStore = defineStore('circle', {
  state: () => ({
    cookieCode: '', typedCode: '', discountCents: 0, message: '',
    // affiliate session (separate identity from the customer login)
    token: (() => { try { return localStorage.getItem(TOKEN_KEY) || ''; } catch (e) { return ''; } })(),
    me: null,
    stats: null,
  }),
  getters: {
    // explicit code beats the cookie — the server enforces this too
    effectiveCode: (s) => (s.typedCode || s.cookieCode || '').trim().toUpperCase(),
    source: (s) => (s.typedCode ? 'explicit' : 'cookie'),
  },
  actions: {
    /** Call once on app start. Records the click and remembers the code for 30 days. */
    captureFromUrl() {
      const code = new URLSearchParams(window.location.search).get('ref');
      if (code) {
        writeCookie(code.trim().toUpperCase());
        axios.post('/circle/click/', { code, path: window.location.pathname }).catch(() => {});
      }
      this.cookieCode = readCookie();
    },
    async validate(subtotal) {
      const code = this.effectiveCode;
      if (!code) { this.discountCents = 0; this.message = ''; return; }
      try {
        const r = await axios.post('/circle/validate-code/', { code, subtotal });
        this.discountCents = r.data.discount_cents || 0;
        this.message = r.data.message || '';
      } catch (e) {
        // Never block checkout on a referral lookup.
        this.discountCents = 0;
        this.message = '';
      }
    },

    // ---------- affiliate portal (X-Circle-Token; never the customer JWT) ----------
    _h() { return { headers: { 'X-Circle-Token': this.token } }; },
    setToken(t) {
      this.token = t || '';
      try {
        if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY);
      } catch (e) { /* private mode */ }
    },
    async fetchStats() {
      this.stats = (await axios.get('/circle/stats/')).data;
      return this.stats;
    },
    async signup(payload) { return (await axios.post('/circle/signup/', payload)).data; },
    async requestCode(email) { return (await axios.post('/circle/request-code/', { email })).data; },
    async verify(email, code) {
      const d = (await axios.post('/circle/verify/', { email, code })).data;
      this.setToken(d.token);
      return d;
    },
    async logoutAffiliate() {
      try { await axios.post('/circle/logout/', {}, this._h()); } catch (e) { /* best effort */ }
      this.setToken('');
      this.me = null;
    },
    async fetchMe() {
      this.me = (await axios.get('/circle/me/', this._h())).data;
      return this.me;
    },
    async saveProfile(payload) {
      // payload may be FormData (avatar upload) or a plain object
      return (await axios.post('/circle/profile/', payload, this._h())).data;
    },
    async acceptDocument(slug) {
      return (await axios.post('/circle/accept/', { slug, accepted: true }, this._h())).data;
    },
    async claimCredit() { return (await axios.post('/circle/claim-credit/', {}, this._h())).data; },
    async fetchLegal(slug) { return (await axios.get(`/circle/legal/${slug}/`)).data; },
    async fetchSchedule() { return (await axios.get('/circle/schedule/')).data; },
    async fetchPartner(slug) { return (await axios.get(`/circle/partner/${slug}/`)).data; },
  },
});
